<template>
  <slot name="loading" :loading="loading">
    <div v-if="loading" class="tw-flex tw-items-center tw-justify-center">
      <WdsSpinner indeterminate />
    </div>
  </slot>

  <WdsText v-if="!loading && contextMessage" secondary>
    {{ contextMessage }}
  </WdsText>

  <div
    v-for="(mappedField, index) in mappedAndSortedFields"
    :key="mappedField.fieldTyped.id"
    class="tw-space-y-2"
  >
    <CustomField
      :data-test="`custom-field-input-${index}`"
      :field="mappedField.field"
      :model-value="mappedField.fieldValue"
      :error="errors?.[mappedField.fieldTyped.id]?.value"
      :force-enabled="props.forceEnableInputs"
      @update:model-value="(value) => onFieldValueChange(mappedField.fieldTyped.id, value)"
    />
  </div>
</template>

<script setup lang="ts">
import { toRef, Ref } from 'vue'
import { WdsSpinner, WdsText } from '@worksome/wds-components'

import { FragmentType } from '@/gql'
import { CustomFieldTypeValueInput } from '@/gql/graphql'

import CustomField from '@/components/Fieldable/CustomField.vue'
import useFormCustomFieldValues from '@/components/Fieldable/composables/useFormCustomFieldValues'
import { FieldableFormCustomFieldFragment } from '@/components/Fieldable/graphql/fragments'
import { useTranslation } from '@/composables/translation'

const emits = defineEmits(['update:field-values'])
const { $t } = useTranslation()

interface Props {
  fields: FragmentType<typeof FieldableFormCustomFieldFragment>[]
  fieldValues: CustomFieldTypeValueInput[]
  loading?: boolean
  errors?: { [key: string]: Ref<string> }
  contextMessage?: string
  forceEnableInputs?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  errors: undefined,
  forceEnableInputs: false,
})

const { mappedAndSortedFields, parseUpdateFieldValues } = useFormCustomFieldValues(
  toRef(props, 'fields'),
  toRef(props, 'fieldValues'),
)

const onFieldValueChange = (fieldId: string, value: CustomFieldTypeValueInput) => {
  emits('update:field-values', parseUpdateFieldValues(fieldId, value))
}
</script>
